// Proofs of residence permit extension (temp_worker.visa.complementaryDocuments).
// Status codes: '0' draft the temp worker has not confirmed yet (never shown to an
// admin), '1' waiting for Jobbiz, '2' validated, '3' refused.

export const VISA_COMPLEMENTARY_TYPE_LABELS = {
  new_permit: 'Nouveau titre de séjour',
  receipt: 'Récépissé de renouvellement',
  anef_extension: "Attestation de prolongation d'instruction (ANEF)",
};

const DISPLAYABLE_STATUSES = ['1', '2', '3'];

const CALENDAR_DATE = /^(\d{4})-(\d{2})-(\d{2})$/;

/**
 * Label of a proof type, read from the locales when a language is given.
 * @param {string} type - 'new_permit' | 'receipt' | 'anef_extension'
 * @param {object} [language] - fLang instance from the context
 * @returns {string}
 */
export const getVisaComplementaryTypeLabel = (type, language) => {
  const fallback = VISA_COMPLEMENTARY_TYPE_LABELS[type] || '';
  if (!fallback) return '';
  const localized = language?.getValue?.(
    `visaComplementary.types.${type}`,
    fallback,
  );
  return typeof localized === 'string' && localized ? localized : fallback;
};

const getComplementaryDocuments = (tempWorker) => {
  const documents = tempWorker?.visa?.complementaryDocuments;
  return Array.isArray(documents) ? documents : [];
};

const statusOf = (document) =>
  document?.validated === undefined || document?.validated === null
    ? ''
    : String(document.validated);

const timeOf = (value) => {
  const time = value ? new Date(value).getTime() : NaN;
  return Number.isNaN(time) ? 0 : time;
};

/**
 * Proofs an admin may see, newest first. Drafts ('0') belong to the temp
 * worker until they confirm the date, so they are left out.
 * @param {object} tempWorker
 * @returns {object[]}
 */
export const getDisplayableComplementaryDocuments = (tempWorker) =>
  getComplementaryDocuments(tempWorker)
    .filter((document) => DISPLAYABLE_STATUSES.includes(statusOf(document)))
    .sort(
      (a, b) =>
        timeOf(b.createdAt || b.updated) - timeOf(a.createdAt || a.updated),
    );

/**
 * @param {object} tempWorker
 * @param {string|number} status
 * @returns {number}
 */
export const countComplementaryDocumentsWithStatus = (tempWorker, status) =>
  getComplementaryDocuments(tempWorker).filter(
    (document) => statusOf(document) === String(status),
  ).length;

/**
 * @param {object} tempWorker
 * @returns {boolean} true when a proof waits for a Jobbiz admin
 */
export const hasPendingComplementaryDocument = (tempWorker) =>
  countComplementaryDocumentsWithStatus(tempWorker, '1') > 0;

const toIsoDay = (value) => {
  if (value instanceof Date) {
    return Number.isNaN(value.getTime())
      ? ''
      : value.toISOString().slice(0, 10);
  }
  if (typeof value !== 'string') return '';
  const day = value.slice(0, 10);
  return CALENDAR_DATE.test(day) ? day : '';
};

/**
 * Formats a calendar day stored at midnight UTC. Only the first 10 characters
 * are read: going through the local time zone would show the day before in
 * the West Indies (UTC-4).
 * @param {string|Date|null} value - 'AAAA-MM-JJ' or an ISO string
 * @returns {string} 'JJ/MM/AAAA', or '' when there is no usable date
 */
export const formatCalendarDate = (value) => {
  const day = toIsoDay(value);
  if (!day) return '';
  const [, year, month, date] = day.match(CALENDAR_DATE);
  return `${date}/${month}/${year}`;
};

/**
 * Local Date for a calendar day, as the DateInput field expects it.
 * @param {string|Date|null} value
 * @returns {Date|null}
 */
export const calendarDateToLocalDate = (value) => {
  const day = toIsoDay(value);
  if (!day) return null;
  const [, year, month, date] = day.match(CALENDAR_DATE);
  return new Date(Number(year), Number(month) - 1, Number(date));
};

/**
 * 'AAAA-MM-JJ' built from the components of a date typed by the admin, never
 * from toISOString(), which would shift it to UTC.
 * @param {Date|null} date
 * @returns {string} '' when the date is missing or invalid
 */
export const toCalendarDateString = (date) => {
  if (!(date instanceof Date) || Number.isNaN(date.getTime())) return '';
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${date.getFullYear()}-${month}-${day}`;
};

/**
 * True when the temp worker confirmed another end date than the one the AI
 * read. A date the AI could not read is not a mismatch.
 * @param {object} document
 * @returns {boolean}
 */
export const hasEndValidityMismatch = (document) => {
  const read = toIsoDay(document?.end_validity_ai);
  const confirmed = toIsoDay(document?.end_validity);
  return Boolean(read && confirmed && read !== confirmed);
};

/**
 * Effective end of the right to work: the latest day between the permit's own
 * end_validity and the end_validity of the validated proofs ('2'). The permit
 * itself is never updated by a proof, so this has to be computed on display.
 * @param {object} visa - temp_worker.visa
 * @returns {string|Date|null} the winning raw value (keeps the permit's format
 *   for existing date helpers), or null when no date is usable
 */
export const getEffectiveVisaEndValidity = (visa) => {
  const proofs = Array.isArray(visa?.complementaryDocuments)
    ? visa.complementaryDocuments
    : [];
  const candidates = [
    visa?.end_validity,
    ...proofs
      .filter((document) => statusOf(document) === '2')
      .map((document) => document.end_validity),
  ];
  let latest = null;
  let latestDay = '';
  candidates.forEach((value) => {
    const day = toIsoDay(value);
    // ISO days compare correctly as strings.
    if (day && day > latestDay) {
      latest = value;
      latestDay = day;
    }
  });
  return latest;
};

/**
 * The temp worker a panel shows, with the proofs carried by an updateTempWorker
 * signal from the proof modal (the tempWorker returned by the validation or the
 * refusal). Only the proofs are taken: the panel's tempWorker comes from another
 * route and may carry fields the answer lacks.
 * @param {object} tempWorker - the temp worker currently shown
 * @param {object|false} update - value of updateTempWorker in the context
 * @returns {object} the same object when the signal concerns another document
 *   type or another temp worker
 */
export const mergeComplementaryDocumentsUpdate = (tempWorker, update) => {
  const updatedTempWorker = update?.tempWorker;
  if (
    update?.documentType !== 'visa_complementary' ||
    !updatedTempWorker?._id ||
    !tempWorker?._id ||
    String(updatedTempWorker._id) !== String(tempWorker._id)
  )
    return tempWorker;
  const documents = updatedTempWorker.visa?.complementaryDocuments;
  if (!Array.isArray(documents)) return tempWorker;
  return {
    ...tempWorker,
    visa: { ...tempWorker.visa, complementaryDocuments: documents },
  };
};
